import React, { useEffect, useMemo, useRef, useState } from "react";
import "react-tooltip/dist/react-tooltip.css";
import ChartNav from "../../../common/ChartNav";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { searchInterventions, getInterventionById } from "../../../../redux/modules/intervention/actions";
import InterventionList from "../../clinical-services/interventions/interventionList";
import InterventionRecord from "./InterventionRecord";
import { getFirstActiveOrWaitingIntervention, isActiveOrWaitingInterventionStatus } from "./data";

const UNIFIED_VALID_ACTION_PLAN_VALUES = [
  "AdaptPrescription",
  "GiveImmunization",
  "ContactPrescriber",
  "ChangeStartStop",
  "InitiateMonitor",
  "ReferralToPhysician",
  "ReferralToOther",
];

const tabs = [
  "Intervention Details",
  "Targets & Results",
  "Pharmacist Follow Up",
  "Prescriber Follow Up",
  "Notification",
  "Documentation",
  "Billing",
];

const createDefaultInterventionEntity = (
  patientId,
  medicationId,
  medicationName = "",
  clinicalServiceOrigin = "MedicationReview"
) => ({
  id: null,
  patientId,
  medicationId,
  medicationName: medicationName || "[Name of Drug]",
  interventionType: "PharmaceuticalOpinion",
  clinicalServiceOrigin: clinicalServiceOrigin,
  drugTherapyProblem: "",
  actionPlan: UNIFIED_VALID_ACTION_PLAN_VALUES.slice(0, 1),
  urgencyLevel: "Medium",
  comments: "",
  status: "Active",
  createdAt: new Date().toISOString(),
  createdBy: "Current User",
  lastModifiedDate: new Date().toISOString(),
  lastModifiedBy: "Current User",

  interventionDetails: {
    originalRX: null,
    whenDidTheChangeOccur: null,
    whoInitiatedTheChange: null,
    whyDidTheChangeOccur: null,
    pharmacistClinicalDecision: null,
    problemDescription: "",
    assessment: "",
    plan: "",
    expectedOutcome: "",
    riskFactors: "",
    contraindications: "",
    drugInteractions: "",
    monitoringParameters: "",
    followUpSchedule: "",
  },

  targetsAndResults: {
    primaryTarget: "Resolve drug therapy problem",
    secondaryTargets: "",
    successCriteria: "",
    measurableOutcomes: "",
    timeline: "",
    progressIndicators: "",
    riskMitigation: "",
    qualityMetrics: "",
  },

  pharmacistFollowUp: {
    followUpDate: null,
    patientResponse: "",
    effectiveness: "",
    sideEffects: "",
    compliance: "",
    barriers: "",
    recommendations: "",
    nextSteps: "",
    drugDrugInteraction: false,
    drugDiseaseInteraction: false,
    adverseDrugInteraction: false,
    drugLevelMonitoring: false,
    therapyChangeToleranceEffectiveness: false,
    medicationAdherence: false,
    multipleDrugsPolyPharmacy: false,
    complexMedicationRegimen: false,
    recentOrFrequentFalls: false,
    frequentHospitalAdmissions: false,
    chronicDiseaseManagement: false,
    underTreatedConditions: false,
    untreatedConditions: false,
    otherIssue: false,
    otherIssueText: "",
    laboratoryRecommendations: "",
    suggestedLabDateTime: null,
    monitorDrugTherapyEffectiveness: false,
    monitorDrugInteractionsLevels: false,
    monitorDiseaseChronicConditions: false,
    determineRiskStatus: false,
    pharmacistRecommendations: "",
  },

  prescriberFollowUp: {
    originalPrescription: "",
    currentRegimen: "",
    pharmacistNotes: "",
    pharmacistSuggestions: "",
    monitoringSuggestions: "",
    suggestFollowUpWithPrimaryProvider: false,
  },

  notification: {
    notificationType: "",
    dateOfNotification: "",
    methodOfNotification: "",
    physicianName: "",
    physicianContact: "",
    physicianEmail: "",
    physicianPhone: "",
  },

  billing: {
    date: "",
    timeSpent: "00:30:00",
    pharmacistName: "",
    billingState: "",
    interventionOutcome: "",
    pin: "",
    reference: "",
  },

  documentation: {
    documentType: "Intervention Assessment",
    title: "Medication Review",
    content: "",
    author: "",
    version: "1.0",
    reviewDate: new Date().toISOString(),
    approvalStatus: "Draft",
    attachments: "",
  },
});

const PatientIntervention = () => {
  const navigate = useNavigate();
  const { id: patientId, interventionId } = useParams();
  const dispatch = useDispatch();
  // Get data from Redux state
  const { interventions, loading, error } = useSelector(state => state.intervention);
  const { patient } = useSelector(state => state.patients);

  // Tracks that global `loading` has been true at least once — avoids treating pre-fetch state as "loaded empty"
  const [hasSeenLoadingCycle, setHasSeenLoadingCycle] = useState(false);
  const hasNormalizedNoActiveInitialRouteRef = useRef(false);
  useEffect(() => {
    if (loading) setHasSeenLoadingCycle(true);
  }, [loading]);

  // List is trustworthy only after a full fetch cycle (not the initial loading:false frame before useEffect runs search)
  const isInterventionListSettled = hasSeenLoadingCycle && !loading;

  const hasActiveOrWaitingInterventions = useMemo(
    () =>
      Array.isArray(interventions)
      && interventions.some((item) => isActiveOrWaitingInterventionStatus(item?.status)),
    [interventions]
  );

  // Function to search interventions with default values
  const loadInterventions = () => {
    if (patientId) {
      dispatch(searchInterventions(patientId, {
        // Add default search parameters
        status: null, // Get all statuses
        interventionType: null, // Get all types
        clinicalServiceType: null, // Get all clinical services
        pageNumber: 1,
        pageSize: 100 // Get up to 100 interventions
      }));
    }
  };

  // Load interventions on component mount only
  useEffect(() => {
    if (patientId) {
      loadInterventions();
    }
  }, [patientId]); // Only depend on patientId, not loadInterventions function

  // Auto-select once list has finished loading (avoids navigating before Redux list is populated)
  useEffect(() => {
    if (!isInterventionListSettled || interventionId) return;
    if (!Array.isArray(interventions) || interventions.length === 0) return;
    const firstEligible = getFirstActiveOrWaitingIntervention(interventions);
    if (firstEligible?.id) {
      dispatch(getInterventionById(patientId, firstEligible.id));
      navigate(`/patient/${patientId}/interventions/${firstEligible.id}`, { replace: true });
    }
  }, [isInterventionListSettled, interventions, interventionId, patientId, navigate, dispatch]);

  // One-time route normalization: default to base route when no Active/Waiting interventions, while still allowing later Completed/Cancelled selection from the left list.
  useEffect(() => {
    if (!isInterventionListSettled || hasNormalizedNoActiveInitialRouteRef.current) return;

    if (!hasActiveOrWaitingInterventions && interventionId) {
      hasNormalizedNoActiveInitialRouteRef.current = true;
      navigate(`/patient/${patientId}/interventions`, { replace: true });
      return;
    }

    hasNormalizedNoActiveInitialRouteRef.current = true;
  }, [isInterventionListSettled, hasActiveOrWaitingInterventions, interventionId, patientId, navigate]);

  if (!patient) return null;

  return (
    <div className="relative flex flex-col flex-1 min-h-0 bg-white pt-5">
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex flex-1 items-stretch gap-4 min-h-0">
          {/* Left sidebar: static, only vertical scroll */}
          <div className="flex self-stretch h-full">
            <ChartNav />
          </div>

          <InterventionList />

          {/* Right side container: scrollable both axes */}
          <div className="flex flex-1 min-h-0">
            <div className="ml-4 w-full h-full min-h-0">
              {/* Wait for first list fetch before choosing Record vs placeholder (prevents empty-state flash) */}
              {interventionId ? (
                <InterventionRecord interventionId={interventionId} />
              ) : !isInterventionListSettled ? null : hasActiveOrWaitingInterventions ? (
                <div className="w-full h-full flex items-center justify-center bg-white">
                  <div className="text-center">
                    <div className="text-gray-400 text-xl font-medium mb-4">
                      Select an Intervention
                    </div>
                    <div className="text-gray-500 text-base">
                      Choose an intervention from the left panel to view its details.
                    </div>
                  </div>
                </div>
              ) : (
                <InterventionRecord interventionId={interventionId} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientIntervention;